import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Attachment, catchRenderException, DialogServiceAccreditation, Endpoint, ieTabela, ProviderAccreditation, ProviderAccreditationFileService, Route } from '@philips-emr/hpms-provider-accreditation-commons';
import { FormAPI, ModalAPI, PanelAPI, Stamp, StampIcon, ToastNotificationAPI } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnCancel, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ClickEvent, RenderEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HistoryService, HttpService } from '@philips-emr/tws-core/lib/services';

import { ProviderAccreditationAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import './provider-accreditation-controller.css';

interface Accreditation {
  nrSequence: number,
  nrSeqTypeAccreditation: number,
  ieStatus: string,
  nrSeqUser: number;
  nrSeqAccreditation: number,
}

interface CardTypeAccreditation {
  dsAccreditationType: string,
  nrSequence: number,
  message: string,
}

interface DataTerm {
  nameTerm?: string,
  term: string
}

enum AccreditationStatus {
  AWAITING_REQUESTER = '1',
  ANALYSIS = '2',
  AWAITING_REVISION = '3',
  APPROVED = '4',
  CONTRACTED = '5',
  DENIED = '6',
  CHECKING_CNES = '7',
  AWAITING_TECHNICAL_VISIT = '8',
  NEGOTIATING = '9',
  CANCELED = '10'
}

function getStamp(ieStatus: string): ReactElement | null {
  const label = `@pa.provider-accreditation-status/${ieStatus}`;
  let stampProps = null;

  switch (ieStatus) {
    case AccreditationStatus.AWAITING_REQUESTER:
      stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label };
      break;
    case AccreditationStatus.ANALYSIS:
      stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.CLOCK, label };
      break;
    case AccreditationStatus.AWAITING_REVISION:
      stampProps = { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING, label };
      break;
    case AccreditationStatus.APPROVED:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
      break;
    case AccreditationStatus.CONTRACTED:
      stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK, label };
      break;
    case AccreditationStatus.DENIED:
      stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
      break;
    case AccreditationStatus.CHECKING_CNES:
      stampProps = { className: 'c-form-stamp__stamp--color', icon: StampIcon.CLOCK, label };
      break;
    case AccreditationStatus.AWAITING_TECHNICAL_VISIT:
      stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CLOCK, label };
      break;
    case AccreditationStatus.NEGOTIATING:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CLOCK, label };
      break;
    case AccreditationStatus.CANCELED:
      stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
      break;
    default:
      return null;
  }

  return <Stamp {...stampProps} />;
}

enum AccreditationSteps {
  ESTABLISHMENT = '0',
  EMPLOYEES = '1',
  AMOUNT_PROFESSIONALS = '2',
  SPECIALTIES = '3',
  CLINICAL_STAFF = '4',
  PROCEDURES = '5',
  MEDICINE_MATERIALS = '6',
  OUTSOURCED_SERVICES = '7',
  DOCUMENTS = '8',
  QUESTIONNAIRE = '9',
  BEDS = '10',
  EQUIPMENTS = '11',
  QUALIFICATIONS = '12',
  PACKAGES = '13',
  SERVICES = '16',
}

type DataValue = Record<string, Value>;
@Controller({ fragment: FragmentNames.PROVIDER_ACCREDITATION })
class ProviderAccreditationController extends BaseController {

  private attachmentTemplate: Attachment | null = null;

  @Inject(Aliases.PANEL_TERM)
  private panelTerm: PanelAPI;
  @Inject(Aliases.MODAL_TERM)
  private modalTerm: ModalAPI;
  @Inject(Aliases.PANEL_LINK_GUINDACE_MANUAL)
  private panelGuidanceManual: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toastNotification: ToastNotificationAPI;
  @Inject(Aliases.FORM_TERM)
  private formTerm: FormAPI;

  @OnMount(Aliases.PANEL_PROVIDER_ACCREDITATION_LIST)
  public onMountPanelProviderAccreditationList(): void {
    const { nrSeqUser } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqUser });
  }

  @OnMount(Aliases.LAYOUT_PROVIDER_ACCREDITATION)
  onMountPanelFileTemplates(): void {
    void HttpService.get<Attachment>(Endpoint.GET.GUIDANCE_MANUAL)
      .then(resp => {
        const { body = null } = resp;
        this.attachmentTemplate = body;
        catchRenderException(() => {
          this.panelGuidanceManual.setVisible(Boolean(this.attachmentTemplate?.filename));
        });
      }).catch(() => {
        catchRenderException(() => {
          this.panelGuidanceManual.setVisible(false);
        });
      });
  }

  private gotoDetail(nrSeqAccreditation: number, nrSeqTypeAccreditation: number): void {
    HistoryService.push(Route.DETAIL_PROVIDER_ACCREDITATION, { nrSeqAccreditation, nrSeqTypeAccreditation });
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD_PROVIDER_ACCREDITATION)
  public onDynamicContentRender(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus } = data as unknown as ProviderAccreditation;
    const stampExclusao = getStamp(ieStatus);

    if (stampExclusao) {
      render((): ReactElement => stampExclusao);
    }

  }

  @OnClose(Aliases.MODAL_TERM)
  closeModal(): void {
    this.panelTerm.setVisible(false);
  }

  @OnClick(Aliases.LINK_GUIDANCE_MANUAL)
  onClickLink(): void {
    if (this.attachmentTemplate) {
      const { nrSeqUser } = this.historyService.getCurrentRouteParams() as unknown as Accreditation;
      ProviderAccreditationFileService.download(nrSeqUser, ieTabela.PROVIDER_ACCREDITATION_GUIDANCE_MANUAL, this.attachmentTemplate.nrSeqAttachment, this.attachmentTemplate.filename);
    }
  }

  @OnClick(Aliases.TRAIL_ACCREDITATION)
  onClick(event: ClickEvent): void {
    const api = event.value as CardTypeAccreditation;
    const { nrSeqUser } = this.historyService.getCurrentRouteParams();
    const nrSeqTypeAccreditation = api.nrSequence;
    const messageSuspension = api.message;
    this.context.set({ nrSeqTypeAccreditation: api.nrSequence });

    if (messageSuspension) {
      DialogServiceAccreditation.alert(messageSuspension);
    } else {
      void HttpService.get<DataTerm>(Endpoint.GET.TYPE_ACCREDITATION, { nrSeqTypeAccreditation, nrSeqUser }).then(resp => {
        const data = resp.body;
        if (!data || (data && Object.keys(data).length == 0)) {
          catchRenderException(() => this.actionBtnConfirm());
          return;
        }
        const { nameTerm = '', term } = data;
        this.panelTerm.setVisible(true);
        this.modalTerm.setTitle(nameTerm);
        this.formTerm.setData({ term, nrSeqTypeAccreditation, nrSeqUser });
      });
    }
  }

  @OnSubmit(Aliases.FORM_TERM)
  onSubmitFormTerm(event: SubmitEvent): void {
    const { record } = event;
    if (!record || !record.ieTerms) {
      this.toastNotification.setVisible(true);
      this.scrollDownPanelTerm();
      return;
    }
    this.actionBtnConfirm();
  }

  scrollDownPanelTerm(): void {
    const termPanel = document.querySelector<HTMLDivElement>('.c-modal__content--with-stack-manager');
    if (termPanel) {
      const { scrollHeight } = termPanel;
      termPanel.scrollTo({ top: scrollHeight, behavior: 'smooth' });
    }
  }

  private actionBtnConfirm(): void {
    const nrSeqTypeAccreditation = this.context.get('nrSeqTypeAccreditation') as number;
    this.historyService.push(Route.NEW_PROVIDER_ACCREDITATION, { nrSeqTypeAccreditation });
  }

  @OnCancel(Aliases.FORM_TERM)
  onCancelFormTerm(): void {
    this.panelTerm.setVisible(false);
  }

  @OnSelectItem(Aliases.DECK_PROVIDER_ACCREDITATION)
  public onSelectItemBeneficiarios(event: SelectItemEvent<DataValue>): void {
    const data = event.item as unknown as Accreditation;
    this.gotoDetail(data.nrSequence, data.nrSeqTypeAccreditation);
  }
}

export {
  ProviderAccreditationController,
  AccreditationSteps,
  AccreditationStatus,
  Accreditation,
};
